/**
 * @fileoverview Update User mutation hook.
 * @description Wraps PATCH /users/{userId} in a TanStack mutation. On success it refreshes every
 * User Management query (the directory, its summary and filter options, and the user's details),
 * since a role or manager change moves the user in all of them, then runs the dialog's completion
 * callback. The callback is an option of the mutation, not of a `mutate` call, so it still runs if
 * the dialog unmounts while the request is in flight.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { USERS_QUERY_KEY } from '@/features/users/constants/users-query-keys.constants'
import { updateUser } from '@/features/users/services/users.service'

import type { UpdateUserRequest, VersionedUserDetail } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

export interface UpdateUserVariables {
  userId: string
  payload: UpdateUserRequest
  /** The ETag of the details the edit started from. */
  etag: string
}

/**
 * @description Updates a user.
 * @param {{ onSuccess?: (result: VersionedUserDetail) => void }} [options] - What to do with the
 * updated user after a success.
 * @returns {import('@tanstack/react-query').UseMutationResult<VersionedUserDetail, ApiError,
 * UpdateUserVariables>} The mutation object (mutate, isPending, error, ...).
 */
export function useUpdateUser({
  onSuccess,
}: { onSuccess?: (result: VersionedUserDetail) => void } = {}) {
  const queryClient = useQueryClient()

  return useMutation<VersionedUserDetail, ApiError, UpdateUserVariables>({
    /**
     * @description Sends the update against the version the form was filled from.
     * @param {UpdateUserVariables} variables - The user, the changed fields and the ETag.
     * @returns {Promise<VersionedUserDetail>} The updated user and their new version.
     */
    mutationFn: ({ userId, payload, etag }) => updateUser(userId, payload, etag),
    /**
     * @description Refreshes the User Management queries, then reports the updated user.
     * @param {VersionedUserDetail} result - The updated user and their new version.
     */
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY })
      onSuccess?.(result)
    },
  })
}
